import { useState } from 'react'

import { AddFilterButton, Filters } from '@/components'
import {
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  ChartControls,
  ChartFilters,
  FigureLabel,
  FigureValue,
} from '@/components/ui'
import type { ChartControlsState } from '@/components/ui/chart/types'
import {
  type Transaction,
  useProject,
  useStatementCoverage,
} from '@/modules/projects'
import {
  applyFilters,
  type FieldSchema,
  FiltersProvider,
  useFilters,
} from '@/stores'
import { amountTone, cn, numberToCurrency } from '@/utils'
import { CaseActivityTimeline } from './CaseActivityTimeline'
import { buildCaseActivity, resolveCaseSpan } from './caseActivity'
import { ProcessingCard } from './ProcessingCard'

interface CaseActivityCardProps {
  transactions: Transaction[]
}

const CHART_HEIGHT = 320
const FILTER_CONTEXT_ID = 'overview-account-activity'

// The same field the transactions table filters on, so the account filter here
// behaves exactly like the one there.
const ACCOUNT_FILTER_SCHEMA: FieldSchema[] = [
  { field: 'accountNumber', label: 'Account', type: 'select', multiple: true },
]

// Distinct accounts as filter options, labeled like the transactions table
// (institution, type, transaction count).
const buildAccountValues = (transactions: Transaction[]) => {
  const byNumber = new Map<
    string,
    { institution: string; accountType: string; count: number }
  >()
  for (const transaction of transactions) {
    const number = transaction.accountNumber
    if (!number) continue
    const existing = byNumber.get(number)
    if (existing) {
      existing.count += 1
    } else {
      byNumber.set(number, {
        institution: transaction.institution || '—',
        accountType: transaction.accountType || '—',
        count: 1,
      })
    }
  }
  return Array.from(byNumber.entries())
    .sort(([first], [second]) => first.localeCompare(second))
    .map(([number, { institution, accountType, count }]) => ({
      value: number,
      label: number,
      description: `${institution} · ${accountType} · ${count} ${
        count === 1 ? 'transaction' : 'transactions'
      }`,
    }))
}

const wholeDollars = (value: number): string =>
  numberToCurrency(value, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })

interface MoneyFigureProps {
  label: string
  value: string
  tone: string
}

const MoneyFigure = ({ label, value, tone }: MoneyFigureProps) => (
  <div className="text-right">
    <FigureLabel>{label}</FigureLabel>
    <FigureValue className={cn('mt-1 whitespace-nowrap text-xl', tone)}>
      {value}
    </FigureValue>
  </div>
)

// The case's money in/out by month, with the same chart controls and filters as the other charts.
// The headline totals give case context, separate from the working stats on the Transactions tab.
export const CaseActivityCard = ({ transactions }: CaseActivityCardProps) => (
  <FiltersProvider contextId={FILTER_CONTEXT_ID}>
    <CaseActivityCardContent transactions={transactions} />
  </FiltersProvider>
)

const CaseActivityCardContent = ({ transactions }: CaseActivityCardProps) => {
  const coverage = useStatementCoverage()
  const project = useProject()
  const filters = useFilters()
  const [chartControls, setChartControls] = useState<ChartControlsState | null>(
    null,
  )

  const filtered = applyFilters(transactions, filters, ACCOUNT_FILTER_SCHEMA)

  // Hold the time axis steady on the whole case span so filtering an account
  // compares like-for-like instead of rescaling.
  const span = resolveCaseSpan(coverage, transactions)
  const activity = buildCaseActivity(filtered, span)
  const getAccountValues = () => buildAccountValues(transactions)

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Filters
          dialogId={FILTER_CONTEXT_ID}
          schema={ACCOUNT_FILTER_SCHEMA}
          getUniqueValues={getAccountValues}
        />
        <ChartFilters>
          <AddFilterButton
            dialogId={FILTER_CONTEXT_ID}
            schema={ACCOUNT_FILTER_SCHEMA}
          />
          {chartControls ? (
            <ChartControls
              containerRef={chartControls.containerRef}
              isFullscreen={chartControls.isFullscreen}
              toggleFullscreen={chartControls.toggleFullscreen}
              fullscreenError={chartControls.fullscreenError}
              fullscreenSupported={chartControls.fullscreenSupported}
              filename={`${project?.slug ?? 'project'}-cash-flow`}
              projectName={project?.name}
              title="Cash flow"
              size="md"
              floating={false}
            />
          ) : null}
        </ChartFilters>
      </div>

      <ProcessingCard>
        <CardHeader className="pb-4">
          <div className="flex flex-wrap items-start justify-between gap-x-8 gap-y-4">
            <div className="min-w-0 flex-1">
              <CardTitle>Cash flow</CardTitle>
              <CardDescription className="mt-1.5 text-pretty text-ink-subtle">
                Money into and out of the bank accounts, by month (excludes
                transfers between the customer's own accounts)
              </CardDescription>
            </div>
            <div className="flex shrink-0 items-start gap-6 sm:gap-8">
              <MoneyFigure
                label="Money in"
                value={wholeDollars(activity.totalInflow)}
                tone="text-emerald-600"
              />
              <MoneyFigure
                label="Money out"
                value={wholeDollars(activity.totalOutflow)}
                tone="text-red-600"
              />
              <MoneyFigure
                label="Net"
                value={wholeDollars(activity.net)}
                tone={amountTone(activity.net)}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {activity.months.length === 0 ? (
            <div className="flex h-[150px] items-center justify-center text-center text-sm text-muted-foreground">
              No dated activity yet. Upload statements on the Files tab.
            </div>
          ) : (
            <div style={{ height: CHART_HEIGHT }}>
              <CaseActivityTimeline
                months={activity.months}
                onChartReady={setChartControls}
              />
            </div>
          )}
        </CardContent>
      </ProcessingCard>
    </div>
  )
}
